import { useApolloClient, useQuery } from '@apollo/client';
import type { DirectDealsListFilters } from '@openx/components-direct-deals/types';
import { type CriteriaChange, CriteriaDimension, SortDirection } from '@openx/types/tableCriteria';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria';
import { useCallback, useMemo } from 'react';
import {
  GetCuratedDealBuyerDiscountListDocument,
  type GetCuratedDealBuyerDiscountListQuery,
  type GetCuratedDealBuyerDiscountListQueryVariables,
} from '../../../../graphql/query/curatedDealBuyerDiscount/GetCuratedDealBuyerDiscountList.generated';
import type { CuratedDealBuyerDiscountApi } from '../../../../types/curatedDealBuyerDiscount';
import { findLastPopulatedPage } from '../../../../utils/findLastPopulatedPage';
import { handlePaginationUpdate } from '../../../../utils/handlePaginationUpdate';
import { useLastPopulatedPageRedirect } from '../../../../utils/useLastPopulatedPageRedirect';
import { useListCriteria } from '../../../../utils/useListCriteria';
import { curatedDealBuyerDiscountDefaultCriteria } from '../constants';
import type { CuratedDealBuyerDiscountsListFilters } from '../types';

export const useCuratedDealBuyerDiscountListFetch = () => {
  const apolloClient = useApolloClient();

  const { criteria, setCriteria } = useListCriteria(
    'curatedDealBuyerDiscounts',
    curatedDealBuyerDiscountDefaultCriteria
  );

  const variables = useMemo<GetCuratedDealBuyerDiscountListQueryVariables>(
    () => ({
      params: {
        demand_partner_id: criteria.filters?.demand_partner_id?.join(','),
        limit: criteria.pagination?.pageSize,
        offset: criteria.pagination ? (criteria.pagination.pageNumber - 1) * criteria.pagination.pageSize : undefined,
        q: criteria.filters?.phrase,
        sort: criteria.sort?.direction === SortDirection.ASC ? criteria.sort.column : `-${criteria.sort?.column}`,
      },
    }),
    [criteria]
  );

  const { data, loading } = useQuery(GetCuratedDealBuyerDiscountListDocument, {
    fetchPolicy: 'network-only',
    variables,
  });

  const discountData = data?.curatedDealBuyerDiscount;

  const requestedPage = criteria.pagination?.pageNumber ?? 1;
  const pageSize = criteria.pagination?.pageSize ?? curatedDealBuyerDiscountDefaultCriteria.pagination.pageSize;

  const objectsLength = discountData?.objects?.length ?? 0;
  const hasMore = discountData?.has_more;

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension, DirectDealsListFilters>) => {
      setCriteria(previousCriteria => applyCriteriaChange(previousCriteria, newCriteria));
    },
    [setCriteria]
  );

  const handleFiltersChange = useCallback(
    (newFilters: CuratedDealBuyerDiscountsListFilters) => {
      handleCriteriaChange({
        dimension: CriteriaDimension.FILTERS,
        value: newFilters,
      });
    },
    [handleCriteriaChange]
  );

  const curatedDealBuyerDiscounts = useMemo<CuratedDealBuyerDiscountApi[]>(
    () => discountData?.objects ?? [],
    [discountData?.objects]
  );

  const resolveLastDiscountPage = useCallback(
    (outOfRangePage: number, abortSignal?: AbortSignal) =>
      findLastPopulatedPage({
        requestedPage: outOfRangePage,
        abortSignal,

        isPagePopulated: async (candidatePage, signal) => {
          const probeOffset = (candidatePage - 1) * pageSize;

          const { data: probeData } = await apolloClient.query<
            GetCuratedDealBuyerDiscountListQuery,
            GetCuratedDealBuyerDiscountListQueryVariables
          >({
            query: GetCuratedDealBuyerDiscountListDocument,
            variables: {
              params: {
                ...variables.params,
                limit: 1,
                offset: probeOffset,
              },
            },
            fetchPolicy: 'no-cache',
            context: {
              fetchOptions: {
                signal,
              },
            },
          });

          return (probeData.curatedDealBuyerDiscount?.objects?.length ?? 0) > 0;
        },
      }),
    [apolloClient, pageSize, variables.params]
  );

  const redirectToDiscountPage = useCallback(
    (correctedPage: number, outOfRangePage: number) => {
      setCriteria(previousCriteria => {
        const previousPagination = previousCriteria.pagination ?? curatedDealBuyerDiscountDefaultCriteria.pagination;

        if (previousPagination.pageNumber !== outOfRangePage) {
          return previousCriteria;
        }

        return {
          ...previousCriteria,
          [CriteriaDimension.PAGINATION]: {
            ...previousPagination,
            pageNumber: correctedPage,
          },
        };
      });
    },
    [setCriteria]
  );

  const updatePaginationNormally = useCallback(() => {
    handlePaginationUpdate({
      defaultPagination: curatedDealBuyerDiscountDefaultCriteria.pagination,
      hasMore,
      isLoading: loading,
      objectsLength,
      setCriteria,
    });
  }, [hasMore, loading, objectsLength, setCriteria]);

  useLastPopulatedPageRedirect({
    enabled: discountData != null,
    requestedPage,
    loading,
    objectsLength,
    hasMore,
    resolveLastPopulatedPage: resolveLastDiscountPage,
    onRedirect: redirectToDiscountPage,
    onRedirectNotNeeded: updatePaginationNormally,
    onFailure: updatePaginationNormally,
  });

  return {
    criteria,
    curatedDealBuyerDiscounts,
    handleCriteriaChange,
    handleFiltersChange,
    loading,
  };
};
